import { filter, guardArray } from "@msuite/utils/array";
import type { OrganizationMembers, Tables } from "@msuite/utils/types";
import type { MentionNodeAttrs } from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import tippy, { type GetReferenceClientRect, type Instance, type Props } from "tippy.js";
import { MentionList, type MentionRenderers, type MentionType } from "./mention-list";
import type { ImageRenderer } from "./image-block-item";
import type { VideoRenderer } from "./video-block-item";

/** Props Interface */
export interface TextareaProps {
	members: OrganizationMembers | undefined;
	projects: Tables<"projects">[] | undefined;
	mentionRenderers: MentionRenderers;
	disableFormatting?: boolean;
	videoRenderer?: VideoRenderer;
	imageRenderer?: ImageRenderer;
}

function prepareMentionItems<T>(items: T[] | undefined, type: MentionType) {
	if (!items || items.length === 0) {
		return [];
	}
	return items.map((item) => ({
		item,
		type,
	}));
}

export function buildMentionExtension({ members, projects, mentionRenderers }: TextareaProps) {
	const memberItems = prepareMentionItems(members, "member");
	const projectItems = prepareMentionItems(projects, "project");

	const suggestions: Omit<SuggestionOptions<any, MentionNodeAttrs>, "editor"> = {
		char: "@",
		items: ({ query }: { query: string }) => {
			if (!query) return [];
			return filter<any>([...guardArray(memberItems), ...guardArray(projectItems)], query);
		},

		render: () => {
			let reactRenderer: ReactRenderer | null = null;
			let popup: Instance<Props>[] = [];

			return {
				onExit() {
					popup?.[0]?.destroy();
					reactRenderer?.destroy();
				},

				onKeyDown(props) {
					if (props.event.key === "Escape") {
						popup?.[0]?.hide();

						return true;
					}

					return (reactRenderer?.ref as any)?.onKeyDown(props);
				},
				onStart: (props) => {
					if (!props.clientRect) return;

					reactRenderer = new ReactRenderer(MentionList, {
						editor: props.editor,
						props: {
							...props,
							mentionRenderers,
						},
					});
					popup = tippy("body", {
						appendTo: () => document.body,
						content: reactRenderer.element,
						getReferenceClientRect: props.clientRect as GetReferenceClientRect,
						interactive: true,
						placement: "bottom-start",
						showOnCreate: true,
						trigger: "manual",
					});
				},

				onUpdate(props) {
					if (!reactRenderer) return null;
					reactRenderer.updateProps(props);

					if (!props.clientRect) {
						return;
					}
					if (!popup?.[0]) return;
					if (!props.clientRect) return;
					popup[0].setProps({
						getReferenceClientRect: props.clientRect as GetReferenceClientRect,
					});
				},
			};
		},
	};

	return suggestions;
}
